<template>
  <div class="signup-form">
    <p class="signup-form__title">
      Создать аккаунт
    </p>
    <p class="signup-form__text">
      Email
    </p>
    <pInput
      name="email"
      :value="signUpData.email"
      :valid="emailIsValid"
      :placeholder="'Введите вашу почту'"
      @input="setEmail"
      @keyup="checkEmail"
    />
    <span class="email-error-text">
      {{ emailErrorText }}
    </span>
    <p class="signup-form__text">
      Ник
    </p>
    <pInput
      :value="signUpData.name"
      :placeholder="'Придумайте ник'"
      @input="signUpData.name = $event.target.value"
    />
    <p class="signup-form__text">
      Пароль
    </p>
    <pInput
      :value="signUpData.password"
      :placeholder="'Придумайте пароль'"
      type="password"
      @input="signUpData.password = $event.target.value"
      @keyup="checkPassword"
    />
    <span class="email-error-text">
      {{ passwordErrorText }}
    </span>
    <pCheckbox
      :modelValue="agreement.years"
      class="signup-form__checkbox"
      :label="'Я подтверждаю, что мне исполнилось 18 лет, и я принимаю пользовательское соглашение.'"
      @update:modelValue="agreement.years = !agreement.years"
    />
    <pButton class="signup-form__sign" @click="sendRequest">
      Зарегистрироваться
    </pButton>
    <span class="signup-form__help">
      Уже есть на аккаунт?
      <span
        class="signup-form__signup"
        @click="authPopupStore.openSignForm"
      >Войти</span>
    </span>
  </div>
</template>

<script setup lang="ts">
import pCheckbox from '../../common/pCheckbox/pCheckbox.vue'
import pButton from '../../common/pButton/pButton.vue'
import pInput from '../../common/pInput/pInput.vue'
import { useAuthPopup } from './../store/authPopup'
import api from "../api/api";
import SignUpData from "../types/SignUpData";
import {Ref, ref} from "vue";
import AgreementSignUp from "@/authSection/types/AgreementSignUp";
import {debounce} from "lodash";
import {AxiosError} from "axios";
import {useClientStore} from "@/authSection/store/clientStore";
import {ValidationSignUpErrorText} from "@/authSection/types/ValidationSignUpErrorText";
import ValidationException from "@/authSection/types/ValidationException";

const authPopupStore = useAuthPopup()
const clientStore = useClientStore()
const signUpData: Ref<SignUpData> = ref({
  email: '',
  password: '',
  name: '',
})
const agreement: Ref<AgreementSignUp> = ref({
  years: true,
})
const emailIsValid: Ref<boolean> = ref(true)
const emailErrorText: Ref<string> = ref('')

const passwordIsValid: Ref<boolean> = ref(true)
const passwordErrorText: Ref<string> = ref('')

function setEmail(event: Event) {
  signUpData.value.email = (event.target as HTMLInputElement).value
}

const checkEmail = debounce(async () => {
  try {
    await api.checkEmail(signUpData.value.email)
    emailErrorText.value = ''
    emailIsValid.value = true
  } catch (e: unknown) {
    if (e instanceof AxiosError) {
      if (e.response?.status === 400) {
        emailIsValid.value = false
        emailErrorText.value = e.response.data.error
      }
    }
  }
}, 500)

const checkPassword = debounce(async () => {
  if (signUpData.value.password.length < 6) {
    passwordErrorText.value = 'Пароль должен быть больше 6 символов'
    return passwordIsValid.value = false
  }
  passwordErrorText.value = ''
  passwordIsValid.value = true
}, 500)

function validateSignUpForm() {
  if (signUpData.value.email.length === 0) {
    throw new ValidationException(ValidationSignUpErrorText.EMAIL_IS_EMPTY)
  }

  if (!emailIsValid.value) {
    throw new ValidationException(ValidationSignUpErrorText.EMAIL_IS_INVALID)
  }

  if (signUpData.value.name.length === 0) {
    throw new ValidationException(ValidationSignUpErrorText.NAME_IS_EMPTY)
  }

  if (signUpData.value.password.length === 0) {
    throw new ValidationException(ValidationSignUpErrorText.PASSWORD_IS_EMPTY)
  }

  if (!passwordIsValid.value) {
    throw new ValidationException(ValidationSignUpErrorText.PASSWORD_IS_INVALID)
  }

  if (!agreement.value.years) {
    throw new ValidationException(ValidationSignUpErrorText.YEARS_AGREEMENT)
  }
}

async function sendRequest() {
  try {
    validateSignUpForm()
  } catch (error: any) {
    if (error instanceof ValidationException) {
      return window.showNotification(error.message, 'warning')
    }
  }

  try {
    await api.createClient({
      ...signUpData.value,
    })
    authPopupStore.visible = false
    clientStore.isAuth = true
  } catch (e: unknown) {
    window.showNotification('Произошла ошибка!', 'error')
    console.log(e)
  }
}
</script>

<style scoped lang="scss">
.signup-form {
  padding: 23px 16px;
  background: #090A0C;
  border-radius: 12px;

  &__title,
  &__text{
    font-family: "Inter", sans-serif;
    font-weight: bold;
  }

  &__title {
    text-align: center;
    font-weight: 700;
    font-size: 16px;
    line-height: 18px;
    margin-bottom: 23px;
  }

  &__text {
    margin: 15px 0 7px;
    font-weight: 700;
    font-size: 14px;
    line-height: 16px;
  }

  &__sign {
    width: 100%;
    margin-top: 12px;
    padding: 12px 0;
  }

  &__help {
    display: block;
    margin: 12px 0 0 0;
    text-align: center;
    color: #737880;
    font: 600 14px/16px Inter, sans-serif
  }

  &__signup {
    color: #fff;
    cursor: pointer;

    @media (hover: hover) {
      &:hover {
        transition: color 0.3s;
        color: #FFED48;
      }
    }
  }

  &__checkbox {
    margin: 12px 0 0 0;
    font-weight: 600;
    font-size: 14px;
    line-height: 16px;
  }
}

.email-error-text{
  color: #F03535;
  margin-top: 5px;
  font-family: "Inter", sans-serif;
  display: block;
}
</style>